import { DateTime } from "luxon";
import React from "react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Grid from "@mui/material/Grid";
import {
  DateTimePicker,
  DateTimePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

import { useOrgTimezone } from "../../../api/organizations/get-organization";
import { theme } from "../../../theme";

type DateTimePickerOnError = DateTimePickerProps<DateTime>["onError"];

interface DateRangePickerProps {
  startFilter: DateTime | null;
  endFilter: DateTime | null;
  resourceMinTime: number;
  resourceMaxTime: number | undefined;
  onEndError: DateTimePickerOnError;
  onStartError: DateTimePickerOnError;
  onTimeChange: (newVal: DateTime | null, filterType: "start" | "end") => void;
}

export const DateRangePicker = ({
  startFilter,
  endFilter,
  resourceMinTime,
  resourceMaxTime,
  onEndError,
  onStartError,
  onTimeChange,
}: DateRangePickerProps) => {
  const timezone = useOrgTimezone(true);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Grid container direction={"column"}>
        <Grid
          container
          direction={"row"}
          columnSpacing={1}
          rowSpacing={1}
          alignItems={"center"}
        >
          <Grid>
            <DateTimePicker
              timezone={timezone}
              slotProps={{
                digitalClockSectionItem: {
                  sx: {
                    "&.Mui-selected": {
                      backgroundColor: `${theme.igBackgroundColors.accentSolid} !important`,
                    },
                  },
                },
              }}
              disableFuture={true}
              minDateTime={DateTime.fromMillis(resourceMinTime)}
              label="Start Time"
              value={startFilter}
              onChange={(newValue) => {
                onTimeChange(newValue, "start");
              }}
              maxDateTime={
                resourceMaxTime
                  ? DateTime.fromMillis(resourceMaxTime)
                  : undefined
              }
              onError={onStartError}
            />
          </Grid>
          <Grid>
            <IgTypography>to</IgTypography>
          </Grid>
          <Grid>
            <DateTimePicker
              timezone={timezone}
              slotProps={{
                digitalClockSectionItem: {
                  sx: {
                    "&.Mui-selected": {
                      backgroundColor: `${theme.igBackgroundColors.accentSolid} !important`,
                    },
                  },
                },
              }}
              disableFuture={true}
              label="End Time"
              minDateTime={DateTime.fromMillis(resourceMinTime)}
              value={endFilter}
              onChange={(newValue) => {
                onTimeChange(newValue, "end");
              }}
              maxDateTime={
                resourceMaxTime
                  ? DateTime.fromMillis(resourceMaxTime)
                  : undefined
              }
              onError={onEndError}
            />
          </Grid>
        </Grid>
        <Grid mt={1}>
          <IgTypography variant={"body2"}>
            <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
              Timezone:{" "}
            </IgTypography>
            {(timezone ?? "").replaceAll("_", " ")}
          </IgTypography>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};
